import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { SAFETY_STATUS_COLORS, RISK_KEYS, SafetyDashboardPayload } from '../../safety-dashboard.model';
import { SafetyDashboardStore } from '../../safety-dashboard.store';
import Chart from 'chart.js/auto';

interface KpiItem {
  title: string;
  value: string;
  desc: string;
  cls: string;
  icon: string;
}

@Component({
  selector: 'app-safety-overview',
  templateUrl: './safety-overview.component.html',
  styleUrls: ['./safety-overview.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SafetyOverviewComponent implements OnInit, OnDestroy {
  loading = false;
  hasData = false;
  kpis: KpiItem[] = [];
  riskStrip: { label: string; value: number }[] = [];

  private sub = new Subscription();
  private charts: any[] = [];
  private payload: SafetyDashboardPayload | null = null;

  private readonly SCATTER_MAX = 500;

  constructor(private store: SafetyDashboardStore, private cdr: ChangeDetectorRef) {
    Chart.defaults.font.family = "'vazir', Tahoma, sans-serif";
  }

  ngOnInit(): void {
    this.sub.add(this.store.loading$.subscribe((v) => { this.loading = v; this.cdr.markForCheck(); }));
    this.sub.add(
      this.store.payload$.subscribe((p) => {
        this.payload = p;
        this.refresh();
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.destroyCharts();
  }

  toEn(n: number | null | undefined, fraction = 1): string {
    if (n === null || n === undefined || isNaN(Number(n))) return '—';
    return Number(n).toLocaleString('en-US', { maximumFractionDigits: fraction });
  }

  riskClass(v: number): string {
    if (v > 40) return 'red';
    if (v > 25) return 'yellow';
    return 'green';
  }

  trackByKpi(_: number, k: KpiItem): string {
    return k.title;
  }

  private refresh(): void {
    const p = this.payload;
    const has = !!p && (!!p.summary || p.vehicles.length > 0);
    this.hasData = has;
    if (!has || !p) {
      this.kpis = [];
      this.riskStrip = [];
      this.destroyCharts();
      this.cdr.markForCheck();
      return;
    }
    this.buildKpis(p);
    this.buildRiskStrip(p);
    this.cdr.markForCheck();
    setTimeout(() => this.renderCharts(p), 60);
  }

  private buildKpis(p: SafetyDashboardPayload): void {
    const s = p.summary;
    if (!s) {
      this.kpis = [];
      return;
    }
    const evaluated = s.TotalVehicles - s.InsufficientDataVehicles;
    this.kpis = [
      { title: 'میانگین امتیاز ایمنی', value: this.toEn(s.AvgSafetyScore), desc: `از ${this.toEn(evaluated, 0)} خودروی قابل ارزیابی`, cls: 'p-blue', icon: 'fa-shield' },
      { title: 'پرریسک / نیازمند توجه', value: this.toEn(s.HighRiskVehicles + s.NeedAttentionVehicles, 0), desc: 'خودروهای بحرانی ناوگان', cls: 'p-red', icon: 'fa-exclamation-triangle' },
      { title: 'مجموع مسافت', value: `${this.toEn(s.TotalDistanceKm)} km`, desc: 'پیمایش ثبت‌شده GPS', cls: 'p-green', icon: 'fa-road' },
      { title: 'مجموع رویدادها', value: this.toEn(s.TotalEvents, 0), desc: 'کل رویدادهای رانندگی', cls: 'p-orange', icon: 'fa-list-alt' },
      { title: 'رویدادهای بسیار پرریسک', value: this.toEn(s.VeryHighRiskEvents, 0), desc: 'نیاز به پیگیری فوری', cls: 'p-red', icon: 'fa-fire' },
      { title: 'کیفیت داده پایین', value: this.toEn(s.LowQualityVehicles, 0), desc: `از ${this.toEn(s.TotalVehicles, 0)} خودرو`, cls: s.LowQualityVehicles > 10 ? 'p-red' : 'p-yellow', icon: 'fa-database' },
    ];
  }

  private buildRiskStrip(p: SafetyDashboardPayload): void {
    const r = p.riskAverages;
    if (!r) {
      this.riskStrip = [];
      return;
    }
    this.riskStrip = [
      { label: 'ریسک سرعت بالا', value: r.AvgHighSpeedRisk },
      { label: 'ریسک کاهش سرعت', value: r.AvgDecelerationRisk },
      { label: 'ریسک شتاب‌گیری', value: r.AvgAccelerationRisk },
      { label: 'ریسک تکرار رفتار', value: r.AvgRepetitionRisk },
      { label: 'ریسک الگوی رانندگی', value: r.AvgDrivingPatternRisk },
    ];
  }

  private destroyCharts(): void {
    this.charts.forEach((c) => {
      try { c.destroy(); } catch {}
    });
    this.charts = [];
  }

  private canvas(id: string): HTMLCanvasElement | null {
    return document.getElementById(id) as HTMLCanvasElement | null;
  }

  private renderCharts(p: SafetyDashboardPayload): void {
    this.destroyCharts();
    if (!this.hasData) return;

    // ---- دونات از Table1 ----
    const donutEl = this.canvas('safetyDonut');
    if (donutEl && p.statusDistribution.length) {
      this.charts.push(
        new Chart(donutEl, {
          type: 'doughnut',
          data: {
            labels: p.statusDistribution.map((x) => x.SafetyStatus),
            datasets: [{
              data: p.statusDistribution.map((x) => x.VehicleCount),
              backgroundColor: p.statusDistribution.map((x) => SAFETY_STATUS_COLORS[x.SafetyStatus] || '#647889'),
              borderWidth: 2,
              borderColor: '#101a24',
            }],
          },
          options: {
            maintainAspectRatio: false,
            cutout: '62%',
            plugins: { legend: { position: 'bottom', labels: { color: '#aebdca', font: { size: 11 }, padding: 8, boxWidth: 10 } } },
          },
        })
      );
    }

    // ---- ۱۰ خودروی بحرانی از Table2 (سرور مرتب‌شده فرستاده) ----
    const top10El = this.canvas('safetyTop10');
    const top10 = (p.topRiskVehicles || []).slice(0, 10);
    if (top10El && top10.length) {
      this.charts.push(
        new Chart(top10El, {
          type: 'bar',
          data: {
            labels: top10.map((x) => (x.strVehicleNo && x.strVehicleNo.length > 15 ? x.strVehicleNo.slice(0, 15) + '…' : x.strVehicleNo || (x.strFullName || '').slice(0, 15))),
            datasets: [{
              label: 'امتیاز ایمنی',
              data: top10.map((x) => Number(x.SafetyScore) || 0),
              backgroundColor: top10.map((x) => (Number(x.SafetyScore) || 0) < 55 ? '#ff5d68' : '#ff9b54'),
              borderRadius: 6,
            }],
          },
          options: {
            indexAxis: 'y',
            maintainAspectRatio: false,
            scales: {
              x: { min: 0, max: 100, ticks: { color: '#8195a6', font: { size: 10 } }, grid: { color: '#1d2b36' } },
              y: { ticks: { color: '#b9c7d2', font: { size: 10 } }, grid: { display: false } },
            },
            plugins: { legend: { display: false } },
          },
        })
      );
    }

    // ---- رادار از Table3 ----
    const radarEl = this.canvas('safetyRadar');
    if (radarEl && p.riskAverages) {
      const r = p.riskAverages;
      this.charts.push(
        new Chart(radarEl, {
          type: 'radar',
          data: {
            labels: RISK_KEYS.map((x) => x.label),
            datasets: [{
              label: 'میانگین ناوگان',
              data: [r.AvgHighSpeedRisk, r.AvgDecelerationRisk, r.AvgAccelerationRisk, r.AvgRepetitionRisk, r.AvgDrivingPatternRisk],
              backgroundColor: '#4da6ff22',
              borderColor: '#5caeff',
              pointBackgroundColor: '#5caeff',
              pointRadius: 4,
            }],
          },
          options: {
            maintainAspectRatio: false,
            scales: { r: { min: 0, max: 100, ticks: { display: false }, grid: { color: '#263746' }, angleLines: { color: '#263746' }, pointLabels: { color: '#b8c6d1', font: { size: 10 } } } },
            plugins: { legend: { display: false } },
          },
        })
      );
    }

    // ---- اسکاتر: نمونه‌برداری از Table8 (نه همه نقاط) ----
    const scatterEl = this.canvas('safetyScatter');
    if (scatterEl && p.vehicles.length) {
      const sampled = sampleArray(p.vehicles, this.SCATTER_MAX);
      this.charts.push(
        new Chart(scatterEl, {
          type: 'scatter',
          data: {
            datasets: [{
              label: 'خودروها',
              data: sampled.map((x) => ({ x: Number(x.DistanceKm) || 0, y: Number(x.SafetyScore) || 0 })) as any,
              backgroundColor: sampled.map((x) => SAFETY_STATUS_COLORS[x.SafetyStatus] || '#647889'),
              pointRadius: 5,
              pointHoverRadius: 8,
            }],
          },
          options: {
            maintainAspectRatio: false,
            scales: {
              x: { title: { display: true, text: 'مسافت (km)', color: '#8ea3b6', font: { size: 11 } }, ticks: { color: '#8195a6', font: { size: 10 } }, grid: { color: '#1d2b36' } },
              y: { title: { display: true, text: 'امتیاز ایمنی', color: '#8ea3b6', font: { size: 11 } }, min: 0, max: 100, ticks: { color: '#8195a6', font: { size: 10 } }, grid: { color: '#1d2b36' } },
            },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => {
                    const item = sampled[ctx.dataIndex];
                    return ` ${item?.strVehicleNo || ''}: ${item?.DistanceKm} km، امتیاز ${item?.SafetyScore}`;
                  },
                },
              },
            },
          },
        })
      );
    }

    // ---- نرخ رویداد از Table6 ----
    const per50El = this.canvas('safetyPer50');
    if (per50El && p.eventsPer50Km) {
      this.charts.push(
        new Chart(per50El, {
          type: 'bar',
          data: {
            labels: ['شتاب شدید', 'ترمز شدید', 'بسیار پرریسک'],
            datasets: [{
              label: 'میانگین در هر ۵۰ کیلومتر',
              data: [p.eventsPer50Km.AccelerationEventsPer50Km, p.eventsPer50Km.DecelerationEventsPer50Km, p.eventsPer50Km.VeryHighRiskEventsPer50Km],
              backgroundColor: ['#5caeff', '#ff8b4c', '#ff5d68'],
              borderRadius: 8,
            }],
          },
          options: {
            maintainAspectRatio: false,
            scales: {
              y: { beginAtZero: true, ticks: { color: '#8195a6', font: { size: 10 } }, grid: { color: '#1d2b36' } },
              x: { ticks: { color: '#b9c7d2', font: { size: 11 } }, grid: { display: false } },
            },
            plugins: { legend: { display: false } },
          },
        })
      );
    }
  }
}

function sampleArray<T>(arr: T[], max: number): T[] {
  if (arr.length <= max) return arr;
  const step = arr.length / max;
  const out: T[] = [];
  for (let i = 0; i < arr.length && out.length < max; i += step) {
    out.push(arr[Math.floor(i)]);
  }
  return out;
}
